import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import { eur } from "@/lib/format";

const EMPTY = { name: "", grade_key: "boerse", direction: "above", threshold: "", recipients: "", active: true };

const METAL_UNITS = { gold: ["g", 0.001, 2], silber: ["kg", 1, 2], aluminium: ["kg", 1, 3], stahl: ["t", 1000, 0] };

export default function AlertFormDialog({ alert, grades, boerse, metals = [], onClose, onSaved }) {
  const api = useApi();
  const [form, setForm] = useState(alert ? { ...alert, recipients: alert.recipients.join(", "), threshold: String(alert.threshold).replace(".", ",") } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const metalKey = form.grade_key.startsWith("metal:") ? form.grade_key.slice(6) : null;
  const [unit, factor, digits] = metalKey ? METAL_UNITS[metalKey] : ["kg", 1, 3];
  const metal = metals.find((m) => m.key === metalKey);
  const current = metalKey ? (metal ? metal.eur_per_kg * factor : null) : form.grade_key === "boerse" ? boerse : grades.find((g) => g.key === form.grade_key)?.price_eur_kg;

  const save = async () => {
    setSaving(true);
    const body = {
      name: form.name, grade_key: form.grade_key, direction: form.direction, active: form.active,
      threshold: parseFloat(String(form.threshold).replace(",", ".")),
      recipients: form.recipients.split(/[,;\s]+/).filter(Boolean),
    };
    try {
      if (alert) await api.put(`/alerts/${alert.id}`, body);
      else await api.post("/alerts", body);
      toast.success("Alarm gespeichert");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-slate-200 bg-white" data-testid="dialog-alert-form">
        <DialogHeader><DialogTitle>{alert ? "Alarm bearbeiten" : "Neuer Preisalarm"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Bezeichnung (optional)</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="z.B. Millberry Verkaufsziel" data-testid="input-alert-name" /></div>
          <div>
            <Label>Sorte / Kurs</Label>
            <Select value={form.grade_key} onValueChange={(v) => set("grade_key", v)}>
              <SelectTrigger data-testid="select-alert-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="boerse">Börsenkurs Kupfer</SelectItem>
                {grades.map((g) => <SelectItem key={g.key} value={g.key}>{g.name}</SelectItem>)}
                {Object.keys(METAL_UNITS).map((k) => (
                  <SelectItem key={k} value={`metal:${k}`}>{metals.find((m) => m.key === k)?.name || k} (€/{METAL_UNITS[k][0]})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="num mt-1 text-xs text-slate-500" data-testid="alert-current-price">Aktuell: {eur(current, digits)}/{unit}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Bedingung</Label>
              <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                <SelectTrigger data-testid="select-alert-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="above">steigt auf ≥</SelectItem>
                  <SelectItem value="below">fällt auf ≤</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Schwellenwert €/{unit}</Label><Input value={form.threshold} onChange={(e) => set("threshold", e.target.value)} inputMode="decimal" placeholder="z.B. 9,50" data-testid="input-alert-threshold" /></div>
          </div>
          <div>
            <Label>Empfänger (E-Mail-Adressen, mit Komma getrennt)</Label>
            <Textarea value={form.recipients} onChange={(e) => set("recipients", e.target.value)} rows={2} placeholder="name@firma.de, chef@firma.de" data-testid="input-alert-emails" />
          </div>
          <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="switch-alert-active" /><Label>Alarm aktiv</Label></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="btn-save-alert">Speichern</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
